import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  EditableColumnDef,
  EditableTable,
} from "../../../../../../shared/components/table/inline-edit-table";
import { CustomSelect, CustomTextField } from "../../../../../../shared/components/inputfields/form-elements";
import { StyledButton } from "../../../../../../shared/components/buttons/custom-button";
import { TCComponentRawMaterialInformation } from "../../../../tc-models/tc-req-dto/tc-component-details.req_dto";
import { useFormActions } from "../../../../../../shared/hooks/form-builder.hooks";
import { dtrackItService } from "../../../../tc-service/dtrackit.service";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";
import useNotification from "../../../../../../shared/hooks/useNotification";
import { Utills } from "../../../../../../shared/app-configs/utilit";


interface RawMaterialsInfoProps {
  rawMaterialInfoData: TCComponentRawMaterialInformation[]; // Expecting an array
  setRawMaterialInfoData: React.Dispatch<
    React.SetStateAction<TCComponentRawMaterialInformation[]>
  >;
}

// Define Yup validation schema for each row


const RawMaterialInfo: React.FC<RawMaterialsInfoProps> = ({
  rawMaterialInfoData,
  setRawMaterialInfoData,
}) => {
  const { notify } = useNotification()
  const {isRestrictPopUpSave, setRestrictPopUpSave } = useTransactionCertificateStore();
  const [rawMaterials, setRawMaterials] = useState([]);
  const [data, setData] = useState<TCComponentRawMaterialInformation[]>(
    rawMaterialInfoData
  ); // Initialize with prop data
  const [tableData, setTableData] = useState<TCComponentRawMaterialInformation[]>(
    rawMaterialInfoData
  ); // Initialize with prop data
  const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);
  const { updateField } = useFormActions("component-details");

  const validationSchema = Yup.object().shape({
    tcComponentRawMaterialCode: Yup.string().nullable(),
    tcComponentRawMaterialPercentage: Yup.number()
      .typeError("Percentage must be a number")
      .positive("Percentage must be positive").max(100, "Percentage cannot exceed 100")
      .test(
        'percentage-equals-100',
        'percentage-equals-100',
        function (value: any) {
          if (editingRowIndex > -1) {
            let total = data.reduce((sum, item, index) => {
              if (index === editingRowIndex) return sum;
              return sum + parseFloat(item.tcComponentRawMaterialPercentage);
            }, 0);
            if ((total + value) > 100) {
              notify("Raw Material Percentage cannot be greater than 100", "error")
              return false
            }
          }
          return true;
  
        }
      )
      .nullable(),
  }).test(
    'at-least-one',
    'At least one field is required',
    function (values) {
      return values.tcComponentRawMaterialCode || values.tcComponentRawMaterialPercentage ? true : this.createError({ path: 'field1', message: 'At least one field is required' });
    }
   );
  useEffect(()=>{
    const getRawMaterialCodes = async () => {
      try {
        const result: any = await dtrackItService.getRawMaterialCodes()

        Utills.cl(`result getProductDetails ${JSON.stringify(result)}`)

        const rawMaterials = result.data.map((t: any) => ({ value: t.code_1, label: `${t.description_1} (${t.code_1})` }))
        Utills.cl(`rawMaterials ${JSON.stringify(rawMaterials)}`)
        setRawMaterials(rawMaterials);
      } catch (err) {
        Utills.cle(`Error in Fetching Organic Farm Standard Code ${err}`)
      }
    };
    getRawMaterialCodes()
  },[])
  const formik = useFormik<TCComponentRawMaterialInformation>({
    initialValues: {
      tcComponentRawMaterialOrder: "",
      tcComponentRawMaterialPercentage: '',
      tcComponentRawMaterialCode: "",
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {
      // Submission logic if needed
    },
  });

  const handleCellChange = (
    rowIndex: number,
    columnId: string,
    value: any
  ) => {
    formik.setFieldValue(columnId, value);
    setData((prevData) =>
      prevData.map((row, index) =>
        index === rowIndex ? { ...row, [columnId]: value } : row
      )
    );
  };

  const handleAddNewRow = () => {
    const newRow: TCComponentRawMaterialInformation = {
      tcComponentRawMaterialOrder: "",
      tcComponentRawMaterialPercentage: '',
      tcComponentRawMaterialCode: "",
    };
    setData((prevData) => [...prevData, newRow]);
    setEditingRowIndex(data.length);
    setRestrictPopUpSave(true)
    formik.setValues(newRow);
    formik.setTouched({});
  };

  const handleCancelEdit = async () => {
    setData(tableData);
    setRestrictPopUpSave(false)
    // Reset editing state and form
    setEditingRowIndex(-1);
    formik.resetForm();
  };
  const handleSaveRow = () => {
    if (editingRowIndex === null || !formik.isValid) return;

    const updatedRow = formik.values;    
    const newData = data.map((row, index) =>
        index === editingRowIndex ? updatedRow : row
      );    
    setData(newData);
    setTableData(newData);
    setRawMaterialInfoData(newData); // Update parent with updated data (not old data)
    setEditingRowIndex(-1);
    setRestrictPopUpSave(false)
    formik.resetForm();
  };

  const columns: EditableColumnDef<TCComponentRawMaterialInformation>[] = [
    {
      accessorKey: "tcComponentRawMaterialCode",
      header: "Raw Material Code",
      renderEditCell: ({ value, onChange }) => (
        <CustomSelect
        name="tcComponentRawMaterialCode"
            label=""
            value={value}
            options={rawMaterials}
            enableSearch={true}
            onChange={(e) => {
              onChange(e.target.value)
            }}
          />
      ),
    },
    {
      accessorKey: "tcComponentRawMaterialPercentage",
      header: "Raw Material Percentage",
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
                  name="tcComponentRawMaterialPercentage"
                  value={value}
                  inputProps={{ max: 100 }}
                  type="number"
                  onChange={(e) => {
                    const newValue = e.target.value;
                    if (!newValue || (Number(newValue) <= 100 && /^[0-9]*$/.test(newValue))) { onChange(newValue) }
                  }
                  }
                  placeholder="Enter value"
                  sx={{ mb: 2 }}
                />

      ),
    },
  ];

  useEffect(() => {
    data.forEach((item: any, index) => {
      item.tcComponentRawMaterialOrder = index + 1;
    });
    updateField("raw-materials-info", data)
  }, [data])

  const handleEditRow = (rowIndex: number) => {
    setEditingRowIndex(rowIndex);
    setRestrictPopUpSave(true);
    formik.setValues(data[rowIndex]);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedData = data.filter((_, index) => index !== rowIndex);
    setData(updatedData);
    setTableData(updatedData);
    setRawMaterialInfoData(updatedData); // Update parent with updated data
  };

  return (
    <div>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 1, border: "2px solid rgba(128, 128, 128, .2)", my: 4 }}
      >
        <Box display="flex" mb={3} justifyContent="flex-end" width="100%">
          <StyledButton disabled={isRestrictPopUpSave} onClick={handleAddNewRow} sx={{ mb: 2 }}>
            Add Raw Material
          </StyledButton>
        </Box>
        <EditableTable
          data={data}
          columns={columns}
          onSaveRow={handleSaveRow}
          onCellChange={handleCellChange}
          editingRowIndex={editingRowIndex}
          onEditRow={handleEditRow}
          onCancelEdit={handleCancelEdit}
          onDeleteRow={handleDeleteRow}
        />
      </Box>
    </div>
  );
};

export default RawMaterialInfo;
